import type { AppConfig } from './types.js';

/**
 * API client.
 *
 * Same-origin in production (Fastify serves the built SPA), proxied in dev, so
 * every path here is relative. The bearer token comes from a callback the auth
 * layer installs, which keeps this module free of any MSAL dependency.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  /** Field-level validation issues, when the API returned a 422. */
  readonly issues: Array<{ path: string; message: string }>;

  constructor(status: number, body: unknown) {
    const record = (body ?? {}) as Record<string, unknown>;
    const message =
      typeof record.message === 'string'
        ? record.message
        : typeof record.error === 'string'
          ? record.error
          : `Request failed with status ${status}`;
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.issues = Array.isArray(record.issues)
      ? (record.issues as Array<{ path: string; message: string }>)
      : [];
  }

  /** True when the caller's role is the problem, not their input. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

type TokenProvider = () => Promise<string | null>;

let getToken: TokenProvider = async () => null;
let devIdentity: string | null = null;

export function setTokenProvider(provider: TokenProvider): void {
  getToken = provider;
}

/**
 * Local development only. Sets the x-dev-user header the API trusts when
 * DEV_AUTH_BYPASS is on, so all five roles are testable without a tenant.
 */
export function setDevIdentity(value: string | null): void {
  devIdentity = value;
  if (value) localStorage.setItem('esm.devIdentity', value);
  else localStorage.removeItem('esm.devIdentity');
}

export function getDevIdentity(): string | null {
  return devIdentity ?? localStorage.getItem('esm.devIdentity');
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  init: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const token = await getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const dev = getDevIdentity();
  if (dev) headers['x-dev-user'] = dev;

  const res = await fetch(path, {
    method,
    headers: { ...headers, ...((init.headers as Record<string, string>) ?? {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...init,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const isJson = (res.headers.get('content-type') ?? '').includes('application/json');
  const parsed = isJson && text ? JSON.parse(text) : text;

  if (!res.ok) throw new ApiError(res.status, parsed);
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

/** Fetched once before the app renders, so auth can be configured correctly. */
export async function loadConfig(): Promise<AppConfig> {
  const res = await fetch('/api/config', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`Could not load app config (HTTP ${res.status})`);
  return (await res.json()) as AppConfig;
}

/** Builds a query string, omitting empty values. */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const str = search.toString();
  return str ? `?${str}` : '';
}
